import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { classifyPoint } from "@/lib/geoClassify";

// Fetches Business Discovery data (bike shops from CRM + restaurants/coffee/
// breweries/hotels from OSM) for a polygon boundary, and holds the layer-toggle
// UI state. Shared by the Bike Paths and Zones maps so they match Trail Lab.
export function useBusinessDiscovery(boundary, opts = {}) {
  const { bikePathId = null, zoneId = null } = opts;
  const [businesses, setBusinesses] = useState([]);
  const [bizStatus, setBizStatus] = useState(null);
  const [bizLoading, setBizLoading] = useState(false);
  const [bizMeta, setBizMeta] = useState({ source: null, unsyncedZones: 0 });
  const [layers, setLayers] = useState({ restaurant: true, bike_shop: false, coffee: false, brewery: false, hotel: false });
  const [bikeFilter, setBikeFilter] = useState("all");
  const [heatOn, setHeatOn] = useState(false);
  const [buffer, setBuffer] = useState(0.5);
  const timerRef = useRef(null);
  const keyRef = useRef(null);

  // Refetch only when the boundary meaningfully changes (rounded bbox + point
  // count) or the corridor width changes — so dragging one vertex doesn't spam.
  const boundaryKey = useMemo(() => {
    const b = boundary || [];
    if (b.length < 3) return null;
    const lats = b.map((p) => p[0]);
    const lngs = b.map((p) => p[1]);
    const bbox = `${Math.min(...lats).toFixed(3)},${Math.min(...lngs).toFixed(3)},${Math.max(...lats).toFixed(3)},${Math.max(...lngs).toFixed(3)}`;
    return `${bikePathId || zoneId || ""}|${buffer}|${b.length}:${bbox}`;
  }, [boundary, bikePathId, zoneId, buffer]);

  const fetchBiz = useCallback(async (bnd, buf) => {
    setBizLoading(true); setBizStatus(null);
    try {
      const { data } = await api.post(`/admin/trails/area-businesses`, {
        boundary: bnd, buffer_miles: buf, bike_path_id: bikePathId, zone_id: zoneId,
      });
      setBusinesses(data.businesses || []); setBizStatus(data.osm_status);
      setBizMeta({ source: data.source || null, unsyncedZones: data.unsynced_zones || 0 });
    } catch (e) {
      setBusinesses([]); setBizStatus("unavailable");
    } finally { setBizLoading(false); }
  }, [bikePathId, zoneId]);

  useEffect(() => {
    if (!boundaryKey) { setBusinesses([]); setBizStatus(null); keyRef.current = null; return undefined; }
    if (keyRef.current === boundaryKey) return undefined;
    keyRef.current = boundaryKey;
    const snapshot = boundary;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fetchBiz(snapshot, buffer), 700);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundaryKey]);

  const refresh = useCallback(() => {
    if ((boundary || []).length >= 3) fetchBiz(boundary, buffer);
  }, [boundary, buffer, fetchBiz]);

  const visibleBiz = useMemo(() => businesses.filter((b) => {
    if (!layers[b.layer]) return false;
    if (b.layer === "bike_shop") {
      if (bikeFilter === "partners") return b.partner;
      if (bikeFilter === "prospects") return !b.partner;
      if (bikeFilter === "non_contacted") return b.outreach_status === "NOT_CONTACTED";
    }
    return true;
  }), [businesses, layers, bikeFilter]);

  const bizCounts = useMemo(() => {
    const c = { restaurant: 0, bike_shop: 0, coffee: 0, brewery: 0, hotel: 0 };
    businesses.forEach((b) => { if (c[b.layer] != null) c[b.layer] += 1; });
    return c;
  }, [businesses]);

  const heatPoints = useMemo(
    () => businesses.filter((b) => b.lat != null && b.lng != null).map((b) => [b.lat, b.lng, 0.6]),
    [businesses]
  );

  const handleShopAction = useCallback(async (shopId, action, note, biz) => {
    if (action === "open_crm") {
      window.dispatchEvent(new CustomEvent("velopaths:open-shop", { detail: { shopId } }));
      return;
    }
    if (action === "add_to_crm") {
      if (!biz) return;
      try {
        const geo = classifyPoint([biz.lat, biz.lng]);
        const { data } = await api.post(`/admin/directory/shops`, {
          name: biz.name, latitude: biz.lat, longitude: biz.lng,
          address: biz.address || null, website: biz.website || null,
          phone: biz.phone || null, state: geo.abbr || null,
        });
        setBusinesses((arr) => arr.map((b) => (
          b.layer === "bike_shop" && !b.shop_id && b.lat === biz.lat && b.lng === biz.lng && b.name === biz.name
            ? { ...b, shop_id: data.id, source: "crm", partner: false, outreach_status: "NOT_CONTACTED" }
            : b)));
        toast.success("Added to your CRM as a prospect");
      } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
      return;
    }
    try {
      if (action === "partner") await api.post(`/admin/directory/shops/${shopId}/convert`, {});
      else await api.post(`/admin/directory/shops/${shopId}/quick-status`, { action, note: note || null });
      const patch = action === "partner"
        ? { partner: true, outreach_status: "ACTIVE_PARTNER" }
        : action === "contacted" ? { outreach_status: "CONTACTED" } : { partner: false };
      setBusinesses((arr) => arr.map((b) => (b.shop_id === shopId ? { ...b, ...patch } : b)));
      toast.success(action === "partner" ? "Invited to partner" : action === "contacted" ? "Marked contacted" : "Marked as prospect");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);

  return { refresh, bizSource: bizMeta.source, unsyncedZones: bizMeta.unsyncedZones, businesses, bizStatus, bizLoading, layers, setLayers, bikeFilter, setBikeFilter, heatOn, setHeatOn, buffer, setBuffer, visibleBiz, bizCounts, heatPoints, handleShopAction };
}
